import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import Button from '../../shared/button.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import SecondaryMenu from '../../menu/secondaryMenu.component';
import DashboardPieChart from '../dashboardPieChart.component';
import { periodOrCurrentSelector, clientNameSelector } from '../../shared/store/selectors';
import {
  fetchInitialDataForQualityOverviewPage,
  preselectTypeAndResource,
  selectPeriodForQualityOverviewPage,
} from '../store/actions';
import { totalProvisionQuality, usefulStats } from '../store/selectors';
import Numeral, { NumeralTypes } from '../../shared/numeral.component';
import { renderDate } from '../../shared/dateHelpers';

import {
  ConnectionsQualityPropTypes,
  BusinessesQualityPropTypes,
  UsefulStatsPropTypes,
} from './propTypes';
import ProvisionQualityPieCharts from './provisionQualityPieCharts.component';
import Spinner from '../../shared/spinner.component';

const getFormattedValue = value => <Numeral type={NumeralTypes.NUMBER}>{value}</Numeral>;

class ProvisionQualityOverviewPage extends PureComponent {
  static propTypes = {
    fetchInitialDataForQualityOverviewPage: PropTypes.func.isRequired,
    isFetchingProvisionsTotalQuality: PropTypes.bool.isRequired,
    isFetchingConnectionsQuality: PropTypes.bool.isRequired,
    isFetchingBusinessesQuality: PropTypes.bool.isRequired,
    isFetchingUsefulStats: PropTypes.bool.isRequired,
    totalProvisionQuality: PropTypes.shape({
      totalSuccess: PropTypes.number.isRequired,
      totalReady: PropTypes.number.isRequired,
      successPercent: PropTypes.number.isRequired,
    }).isRequired,
    connectionsQuality: ConnectionsQualityPropTypes.isRequired,
    businessesQuality: BusinessesQualityPropTypes.isRequired,
    usefulStats: UsefulStatsPropTypes,
    period: PropTypes.shape({
      name: PropTypes.string.isRequired,
      taxYear: PropTypes.string.isRequired,
    }).isRequired,
    periods: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
    })).isRequired,
    selectPeriodForQualityOverviewPage: PropTypes.func.isRequired,
    selectSingleResource: PropTypes.func.isRequired,
    clientName: PropTypes.string.isRequired,
  };

  state = {
    updated: null,
  };

  componentDidMount() {
    this.props.fetchInitialDataForQualityOverviewPage();
    this.setState({
      updated: new Date(),
    });
  }

  refresh = async () => {
    await this.props.fetchInitialDataForQualityOverviewPage();
    this.setState({
      updated: new Date(),
    });
  };

  selectPeriod = async selectedPeriod => {
    const { periods, selectPeriodForQualityOverviewPage } = this.props;

    const value = selectedPeriod ? selectedPeriod.value : null;
    const period = periods.find(period => period.name === value);

    await selectPeriodForQualityOverviewPage(period);

    this.setState({
      updated: new Date(),
    });
  };

  render() {
    const {
      isFetchingProvisionsTotalQuality,
      isFetchingConnectionsQuality,
      isFetchingBusinessesQuality,
      isFetchingUsefulStats,
      totalProvisionQuality,
      connectionsQuality,
      businessesQuality,
      usefulStats,
      selectSingleResource,
      periods,
      clientName,
    } = this.props;

    const { updated } = this.state;
    const lastUpdateTime = updated ? renderDate(updated) : null;

    const periodsForSelect = periods.map(({ name }) => ({
      value: name,
      label: name,
    }));

    return (
      <div className="provision-quality-overview-page">
        <SecondaryMenu>
          <PeriodDropdown
            disabled={!periodsForSelect.length}
            onChange={this.selectPeriod}
          />
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid">
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Provision Quality Overview</h5>
                  <div>
                    Updated: {lastUpdateTime} <Button type="button" kind="primary" onClick={this.refresh}>Refresh</Button>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-6 col-lg-2">
                    {isFetchingProvisionsTotalQuality ? <Spinner/> : (
                      <div className="stat-pie-chart">
                        <DashboardPieChart
                          label={clientName}
                          percentage={Math.floor(totalProvisionQuality.successPercent * 100)}
                          summaryText={(
                            <span>
                              {getFormattedValue(totalProvisionQuality.totalSuccess)}{' '}
                              of{' '}
                              {getFormattedValue(totalProvisionQuality.totalReady)}
                            </span>
                          )}
                          text="Tax Provisions successfully completed."
                        />
                      </div>
                    )}
                  </div>

                  <div className="col-sm-12 col-lg-4">
                    {isFetchingUsefulStats ? (
                      <div className="stat text-dark">{<Spinner/>}</div>
                    ) : (
                      <div className={`stat stat-1 ${usefulStats.stat1.color}`}>
                        <div className="text-top">
                          {usefulStats.stat1.stat}
                        </div>
                        <div className="text-bottom">
                          {usefulStats.stat1.value}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-sm-12 col-lg-4">
                    {isFetchingUsefulStats ? (
                      <div className="stat text-dark">{<Spinner/>}</div>
                    ) : (
                      <div className={`stat stat-2 ${usefulStats.stat2.color}`}>
                        <div className="text-top">
                          {usefulStats.stat2.stat}
                        </div>
                        <div className="text-bottom">
                          {usefulStats.stat2.value}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="col-sm-6 col-lg-2">
                    {isFetchingUsefulStats ? (
                      <div className="stat text-dark">{<Spinner/>}</div>
                    ) : (
                      <div className={`stat stat-3 ${usefulStats.stat3.color}`}>
                        <div className="text-top">
                          {usefulStats.stat3.value}
                        </div>
                        <div className="text-bottom">
                          {usefulStats.stat3.stat}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {isFetchingConnectionsQuality || isFetchingBusinessesQuality
                  ? (
                    <div className="d-flex justify-content-around">
                      <Spinner/>
                    </div>
                  ) : (
                    <ProvisionQualityPieCharts
                      connectionsQuality={connectionsQuality}
                      businessesQuality={businessesQuality}
                      selectSingleResource={selectSingleResource}
                    />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { dashboard: { provisionQuality }, shared } = state;

  return {
    isFetchingProvisionsTotalQuality: provisionQuality.isFetchingProvisionsTotalQuality,
    isFetchingConnectionsQuality: provisionQuality.isFetchingConnectionsQuality,
    isFetchingBusinessesQuality: provisionQuality.isFetchingBusinessesQuality,
    isFetchingUsefulStats: provisionQuality.isFetchingUsefulStats,
    totalProvisionQuality: totalProvisionQuality(state),
    connectionsQuality: provisionQuality.connectionsQuality,
    businessesQuality: provisionQuality.businessesQuality,
    usefulStats: usefulStats(state),
    period: periodOrCurrentSelector(state),
    periods: shared.periods,
    clientName: clientNameSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForQualityOverviewPage,
    selectPeriodForQualityOverviewPage,
    selectSingleResource: preselectTypeAndResource,
  }
)(ProvisionQualityOverviewPage);
